import React, { useEffect, useMemo, useState } from 'react';
import { Link, useHistory, useLocation, useParams } from 'react-router-dom';
import { C10S_SITE_LABEL, C10sProductRow, C10sSite, C10sTexture, C10sVariant, getC10sProduct } from '../../api/assets';
import { ApiError } from '../../api/request';
import ImageLightbox from '../../components/ImageLightbox';
import {
    areaText, areaTextWithPos, fmtUnix, MerchantBadge, ParamBadge, ParamTable, processTechText, PUBLISH_LABEL, storeTypeText,
    textureShowTypeText,
} from './c10sLabels';
import { ProductImg } from './productLabels';
import { thumbUrl, THUMB_W_SM } from '../../utils/imgUrl';

// c10s 商家商品详情: 商家 · 底图与可设计区域 · 各 SKU 的加工参数 (功率/速度/次数…) · 材质 · 原文.
// 「加工参数」是本页的重点: 列表只标有/无, 具体参数值在这里逐面逐条列出 (需求原文).

function Section({ title, hint, children, right }: { title: string; hint?: React.ReactNode; children: React.ReactNode; right?: React.ReactNode }) {
    return (
        <div className="card p-4">
            <div className="flex items-baseline gap-2 mb-2">
                <div className="text-sm font-semibold text-slate-700">{title}</div>
                {hint && <div className="text-[11px] text-slate-400">{hint}</div>}
                {right && <div className="ml-auto">{right}</div>}
            </div>
            {children}
        </div>
    );
}

function Fact({ label, children }: { label: string; children: React.ReactNode }) {
    return (
        <div className="flex justify-between gap-3 border-b border-slate-50 py-1.5 text-xs">
            <span className="text-slate-400 shrink-0">{label}</span>
            <span className="text-slate-700 text-right break-words min-w-0">{children}</span>
        </div>
    );
}

// posText 参数组的定位信息 → " · 定位 (x, y, z)"; 全 0 / 空不显示 (源侧默认就是全 0).
function posText(pos?: { x?: number; y?: number; z?: number }): string {
    if (!pos) return '';
    const { x = 0, y = 0, z = 0 } = pos;
    return x || y || z ? ` · 定位 (${x}, ${y}, ${z})` : '';
}

// TextureCard 一个材质: 材质图 + 全名 + 字典字段 + 用在哪些 SKU 的哪些面.
// t_texture 只有 title/image/showType/status 四个字段, "更详细"能给的就是把它们摊开
// 再加一条"用在哪儿" (从各 SKU 的面上反查, 详情页本来就有这份数据).
function TextureCard({ tex, usage, onZoom }: {
    tex: C10sTexture;
    usage: { sku: string; sides: number[] }[];
    onZoom: (src: string) => void;
}) {
    const sideCount = usage.reduce((n, u) => n + u.sides.length, 0);
    return (
        <div className="flex items-start gap-3 rounded-xl border border-slate-100 p-3">
            {tex.image ? (
                <ProductImg src={tex.image} className="rounded-lg object-cover ring-1 ring-slate-100 cursor-zoom-in shrink-0"
                    style={{ width: 56, height: 56 }} onClick={() => onZoom(tex.image)} />
            ) : (
                <div className="rounded-lg bg-slate-50 text-[10px] text-slate-400 flex items-center justify-center shrink-0" style={{ width: 56, height: 56 }}>无图</div>
            )}
            <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[13px] font-medium text-slate-800 break-all">{tex.title || `未命名材质 #${tex.id}`}</span>
                    <span className="font-mono text-[11px] text-slate-400">#{tex.id}</span>
                    {tex.status === 'offline' && (
                        <span className="px-1.5 py-0.5 rounded text-[10px] bg-amber-50 text-amber-600" title="源侧材质库已下线, 存量商品仍在引用">已下线</span>
                    )}
                </div>
                <div className="mt-1 text-[11px] text-slate-500">
                    叠加效果 {textureShowTypeText(tex.showType)}
                    <span className="mx-1.5 text-slate-300">·</span>
                    用于 {usage.length} 个 SKU / {sideCount} 个面
                </div>
                {usage.length > 0 && (
                    <div className="mt-1 text-[11px] text-slate-400 break-all">
                        {usage.map((u) => `${u.sku || '(无 SKU)'} 面${u.sides.join('、')}`).join('；')}
                    </div>
                )}
                {tex.image && (
                    <a href={tex.image} target="_blank" rel="noreferrer"
                        className="mt-1 inline-block text-[11px] text-blue-600 hover:underline break-all" onClick={(e) => e.stopPropagation()}>
                        材质图 ↗
                    </a>
                )}
            </div>
        </div>
    );
}

function VariantCard({ v, onZoom }: { v: C10sVariant; onZoom: (src: string) => void }) {
    const [showRaw, setShowRaw] = useState(false);
    const cover = v.skuImage || v.sides.find((s) => s.designImageUrl)?.designImageUrl || '';
    return (
        <div className="rounded-xl border border-slate-100 p-3 space-y-2.5">
            <div className="flex items-start gap-3">
                {cover ? (
                    <ProductImg src={cover} className="rounded-lg object-contain bg-white ring-1 ring-slate-100 cursor-zoom-in shrink-0"
                        style={{ width: 64, height: 64 }} onClick={() => onZoom(cover)} />
                ) : (
                    <div className="rounded-lg bg-slate-50 text-[10px] text-slate-400 flex items-center justify-center shrink-0" style={{ width: 64, height: 64 }}>无图</div>
                )}
                <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                        <span className="font-mono text-[12px] text-slate-700">{v.sku || '—'}</span>
                        {!v.enable && <span className="px-1.5 py-0.5 rounded text-[10px] bg-slate-100 text-slate-400">已停用</span>}
                        <span className="ml-auto text-[12px] text-slate-500">{v.price ? `$${v.price}` : ''}</span>
                    </div>
                    <div className="text-[11px] text-slate-500 mt-1 flex flex-wrap items-center gap-x-2 gap-y-1">
                        {v.options.length === 0 ? <span className="text-slate-400">无规格值</span>
                            : v.options.map((o) => (
                                <span key={`${o.name}-${o.val}`} className="inline-flex items-center gap-1">
                                    {o.color && <i className="w-2.5 h-2.5 rounded-sm ring-1 ring-slate-200" style={{ background: o.color }} />}
                                    {o.name}: <b className="font-medium text-slate-700">{o.val}</b>
                                </span>
                            ))}
                    </div>
                    {(v.designWidth > 0 || v.designHeight > 0) && (
                        <div className="text-[11px] text-slate-500 mt-1" title="源侧 t_product_variant.design_width/height">
                            商品尺寸 <span className="font-mono">{v.designWidth}×{v.designHeight}</span> mm
                        </div>
                    )}
                    {v.parameterGroup && (
                        <div className="text-[11px] text-slate-500 mt-1">
                            参数组「{v.parameterGroup.name || v.parameterGroup.id}」
                            <span className="text-slate-400">
                                {' '}· {v.parameterGroup.deviceCode || '未指定设备'} · {processTechText(v.parameterGroup.processTech)}
                                {posText(v.parameterGroup.processingPosition)}
                            </span>
                        </div>
                    )}
                </div>
            </div>

            {v.sides.map((s, i) => (
                <div key={s.sideId || i} className="rounded-lg bg-slate-50/60 p-2.5 space-y-2">
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-slate-500">
                        <span className="font-semibold text-slate-600" title={`面 id ${s.sideId}`}>
                            面 {i + 1}{s.sideName ? ` · ${s.sideName}` : ''}
                        </span>
                        <span title="坐标是区域左上角 (源侧 printArea.x/y)">
                            {s.printAreas.length === 0 ? '未设可设计区域'
                                : s.printAreas.length === 1 ? `可设计区域 ${areaTextWithPos(s.printAreas[0])}`
                                    : `可设计区域 ${s.printAreas.length} 处: ${s.printAreas.map(areaTextWithPos).join('、')}`}
                        </span>
                        {(s.designWidth > 0 || s.designHeight > 0) && <span>底图 {s.designWidth}×{s.designHeight} mm</span>}
                        {s.texture && (
                            <span className="inline-flex items-center gap-1">
                                材质
                                {s.texture.image && <img src={thumbUrl(s.texture.image, THUMB_W_SM)} alt="" className="w-3.5 h-3.5 rounded-sm object-cover" loading="lazy" decoding="async" />}
                                {s.texture.title || `#${s.texture.id}`}
                            </span>
                        )}
                        {s.machine && <span>设备 {s.machine}</span>}
                        {s.processTech && <span>{processTechText(s.processTech)}</span>}
                        {s.processingMode && <span className="font-mono">{s.processingMode}</span>}
                        {s.parameterGroupId && (
                            <span title={`面级参数组 #${s.parameterGroupId}（企业店铺按面绑定）`}>
                                参数组 {v.paramGroups?.[s.parameterGroupId]?.name || `#${s.parameterGroupId}`}
                            </span>
                        )}
                        {s.designImageUrl && (
                            <button type="button" className="ml-auto text-blue-600 hover:underline" onClick={() => onZoom(s.designImageUrl)}>看底图</button>
                        )}
                    </div>
                    <ParamTable parameters={s.parameters} />
                </div>
            ))}

            <div>
                <button type="button" className="text-[11px] text-slate-400 hover:text-slate-600" onClick={() => setShowRaw(!showRaw)}>
                    {showRaw ? '收起源侧原文' : '展开源侧原文 (side_designs)'}
                </button>
                {showRaw && (
                    <pre className="mt-1.5 text-[10px] leading-relaxed bg-slate-900 text-slate-100 rounded-lg p-2.5 overflow-x-auto max-h-72">
                        {JSON.stringify(v.sides.map((s) => s.raw), null, 2)}
                    </pre>
                )}
            </div>
        </div>
    );
}

function C10sDetail() {
    const { id } = useParams<{ id: string }>();
    const history = useHistory();
    // 看哪个站点的库, 由列表页带过来的 ?site 决定 (缺省美服).
    const site: C10sSite = new URLSearchParams(useLocation().search).get('site') === 'cn' ? 'cn' : 'us';
    const [data, setData] = useState<{ product: C10sProductRow; variants: C10sVariant[] } | null>(null);
    const [error, setError] = useState('');
    const [zoomSrc, setZoomSrc] = useState<string | null>(null);

    useEffect(() => {
        let alive = true;
        setData(null);
        setError('');
        getC10sProduct(id, site).then((r) => { if (alive) setData(r); })
            .catch((e) => { if (alive) setError(e instanceof ApiError ? e.message : String(e)); });
        return () => { alive = false; };
    }, [id, site]);

    const p = data?.product;
    const variants = useMemo(() => data?.variants || [], [data]);
    const images = useMemo(() => {
        const all = [...(p?.baseImages || []), ...(p?.carouselImages || [])];
        if (p?.thumbnailUrl) all.push(p.thumbnailUrl);
        return Array.from(new Set(all.filter(Boolean)));
    }, [p]);
    // 材质 → 用在哪些 SKU 的哪些面 (面序号从 1 起, 与"规格与加工参数"里的编号一致).
    const textureUsage = useMemo(() => {
        const m = new Map<string, { sku: string; sides: number[] }[]>();
        variants.forEach((v) => {
            v.sides.forEach((sd, i) => {
                if (!sd.texture) return;
                const list = m.get(sd.texture.id) || [];
                const hit = list.find((u) => u.sku === v.sku);
                if (hit) hit.sides.push(i + 1);
                else list.push({ sku: v.sku, sides: [i + 1] });
                m.set(sd.texture.id, list);
            });
        });
        return m;
    }, [variants]);
    const paramSides = useMemo(
        () => variants.reduce((n, v) => n + v.sides.filter((s) => s.parameters.length > 0).length, 0), [variants]);

    if (error) return <div className="card p-4 text-sm text-rose-600 border-rose-200 bg-rose-50">错误: {error}</div>;
    if (!p) return <div className="text-sm text-slate-400 p-6">加载中…</div>;

    return (
        <div className="space-y-4">
            <div>
                <Link to={`/assets/c10s-products${site === 'us' ? '' : `?site=${site}`}`}
                    className="text-xs text-blue-600 hover:underline">← 返回 商家数据</Link>
                <div className="flex items-start justify-between gap-4 mt-1">
                    <div className="min-w-0">
                        <div className="flex items-center gap-2 text-[11px] text-slate-400 mb-1">
                            <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold text-slate-500"
                                title="这条数据来自哪个站点的 c10s 库">{C10S_SITE_LABEL[site]}</span>
                            <MerchantBadge kind={p.merchant.kind} />
                            <span className="font-semibold text-slate-600">{p.merchant.storeName || '未知商家'}</span>
                            <span>·</span><span>{storeTypeText(p.merchant.storeType)}</span>
                            <span>·</span><span>{processTechText(p.processTech)}</span>
                            {p.publishStatus && <><span>·</span><span>{PUBLISH_LABEL[p.publishStatus] || p.publishStatus}</span></>}
                        </div>
                        <div className="text-lg font-extrabold text-slate-900 tracking-tight leading-snug">{p.title || '无标题'}</div>
                        <div className="text-xs text-slate-400 mt-1">
                            {p.skuCount} 个 SKU · {p.areas.length} 个可设计区域 · {paramSides > 0 ? `${paramSides} 个面配了加工参数` : '未配加工参数'}
                        </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                        {/* 订单在商家数据中心的订单 tab 里看 (跨商品那套筛选/图墙/快照), 这里只给入口 */}
                        {(p.orderCount || 0) > 0 && (
                            <button type="button"
                                className="text-xs px-2.5 py-1 rounded-lg border border-blue-200 text-blue-700 bg-blue-50 hover:bg-blue-100"
                                title="切到商家数据的订单 tab, 并按这个商品筛选"
                                onClick={() => history.push(`/assets/c10s-products?tab=orders&productId=${p.id}`
                                    + `&productTitle=${encodeURIComponent(p.title || '')}${site === 'us' ? '' : `&site=${site}`}`)}>
                                订单 {p.orderCount} →
                            </button>
                        )}
                        <ParamBadge has={p.hasParam} modes={p.paramModes} />
                        <span className="font-mono text-xs px-2 py-1 rounded bg-slate-100 text-slate-600">{p.id}</span>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-[380px_1fr] gap-4 items-start">
                {/* 左: 底图 + 关键信息 */}
                <div className="space-y-3">
                    <Section title="底图" hint={`${images.length} 张 · 含各面切边设计图与轮播图`}>
                        {images.length === 0 ? (
                            <div className="w-full rounded-lg bg-slate-50 flex items-center justify-center text-xs text-slate-400" style={{ aspectRatio: '1 / 1' }}>没有底图</div>
                        ) : (
                            <div className="grid grid-cols-3 gap-2">
                                {images.map((u) => (
                                    <ProductImg key={u} src={u} className="w-full rounded-lg object-contain bg-white border border-slate-100 cursor-zoom-in"
                                        style={{ aspectRatio: '1 / 1' }} onClick={() => setZoomSrc(u)} />
                                ))}
                            </div>
                        )}
                    </Section>

                    <Section title="商家" hint="企业级 = 企业总店 / 企业分店">
                        <Fact label="展示名">{p.merchant.storeName || '—'}</Fact>
                        <Fact label="店铺名">{p.merchant.shopName || '—'}</Fact>
                        <Fact label="店铺类型">{storeTypeText(p.merchant.storeType)}</Fact>
                        <Fact label="店铺 id"><span className="font-mono">{p.merchant.storeId}</span></Fact>
                        {p.merchant.enterpriseId !== '0' && <Fact label="企业 id"><span className="font-mono">{p.merchant.enterpriseId}</span></Fact>}
                    </Section>

                    <Section title="商品" hint="源侧 t_product">
                        <Fact label="加工工艺">{processTechText(p.processTech)}</Fact>
                        <Fact label="设备">{p.devices.length > 0 ? p.devices.join(' / ') : (p.deviceCode || '—')}</Fact>
                        <Fact label="设计模式">{p.designMode || '—'}</Fact>
                        <Fact label="商品类型">{p.productType || '—'}</Fact>
                        <Fact label="面">{p.sideNames.length > 0 ? p.sideNames.join(' / ') : '—'}</Fact>
                        <Fact label="创建时间">{fmtUnix(p.createdAt)}</Fact>
                        <Fact label="源侧更新">{fmtUnix(p.updatedAt)}</Fact>
                    </Section>
                </div>

                {/* 右: 材质 / 可设计区域 / 各 SKU 的加工参数 */}
                <div className="space-y-3">
                    <Section title={`材质 (${p.textures.length})`}
                        hint="源侧 t_texture 字典 · 商家在每个面上各选一个">
                        {p.textures.length === 0 ? (
                            <div className="text-xs text-slate-400">该商品的规格都没选材质（源侧 side_designs.texture 为空）</div>
                        ) : (
                            <div className="space-y-2">
                                {p.textures.map((t) => (
                                    <TextureCard key={t.id} tex={t} usage={textureUsage.get(t.id) || []} onZoom={setZoomSrc} />
                                ))}
                            </div>
                        )}
                    </Section>

                    <Section title={`可设计区域 (${p.areas.length})`}
                        hint={p.enableMultiArea
                            ? '该商品开了多区域: 一个面上可以划多个区域 (源侧 printAreaArr), 下面按形状 + 宽高去重'
                            : '一个面一个区域 (源侧 printArea), 下面按形状 + 宽高去重'}>
                        {p.areas.length === 0 ? <div className="text-xs text-slate-400">该商品未设可设计区域</div> : (
                            <div className="flex flex-wrap gap-2">
                                {p.areas.map((a) => (
                                    <span key={`${a.shape}-${a.width}-${a.height}`} className="px-2 py-1 rounded-lg bg-slate-100 text-slate-700 text-xs font-mono">{areaText(a)}</span>
                                ))}
                            </div>
                        )}
                    </Section>

                    <Section title={`规格与加工参数 (${variants.length} 个 SKU)`}
                        hint="参数取自源侧参数组: 企业店铺按面绑、小商家按变体绑; 空壳参数 (全零占位) 已过滤">
                        {variants.length === 0 ? <div className="text-xs text-slate-400">该商品没有规格</div> : (
                            <div className="space-y-3">
                                {variants.map((v) => <VariantCard key={v.id} v={v} onZoom={setZoomSrc} />)}
                            </div>
                        )}
                    </Section>
                </div>
            </div>
            <ImageLightbox src={zoomSrc} onClose={() => setZoomSrc(null)} />
        </div>
    );
}

export default C10sDetail;
